import {
  frameAutoLayoutMode,
  frameMirrorLayout,
  frameRepeatLayout,
  isPatternAutoLayoutMode,
  type FrameMirrorLayout,
  type FrameNode,
  type FrameRepeatLayout,
  type SceneNode,
} from "@/document/model/nodes";
import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import {
  IDENTITY_AFFINE,
  affineRotation,
  affineScale,
  affineTranslation,
  applyAffinePoint,
  boundsFromAffineRect,
  multiplyAffine,
  type Affine2D,
} from "@/lib/math/affine";
import type { Rect } from "@/lib/math/rect";
import { unionRects } from "@/lib/math/rect";

/**
 * A virtual copy of a pattern-layout child.
 * Index 0 is the editable original. For row/column/grid/mirror it uses the
 * identity transform (document pose). For radial, index 0 is also identity —
 * the document child *is* the first petal; other petals rotate around the ring.
 */
export interface PatternInstance {
  index: number;
  /** Maps original document-space points onto this instance. */
  transform: Affine2D;
  editable: boolean;
}

export function frameUsesPatternLayout(frame: FrameNode): boolean {
  return isPatternAutoLayoutMode(frameAutoLayoutMode(frame));
}

export function patternContentBounds(children: SceneNode[]): Rect | undefined {
  const visible = children.filter(child => child.visible).map(child => child.bounds);
  return unionRects(visible) ?? undefined;
}

export function patternInstanceTransforms(frame: FrameNode): Affine2D[] {
  const mode = frameAutoLayoutMode(frame);
  const content = patternContentBounds(frame.children);
  if (!content) {
    return [IDENTITY_AFFINE];
  }

  if (mode === "repeat") {
    return repeatInstanceTransforms(frameRepeatLayout(frame), content);
  }
  if (mode === "mirror") {
    return mirrorInstanceTransforms(frameMirrorLayout(frame), content);
  }
  return [IDENTITY_AFFINE];
}

export function patternInstancesForFrame(frame: FrameNode): PatternInstance[] {
  return patternInstanceTransforms(frame).map((transform, index) => ({
    index,
    transform,
    editable: index === 0,
  }));
}

export function patternExpandedBounds(frame: FrameNode): Rect | undefined {
  const content = patternContentBounds(frame.children);
  if (!content) {
    return undefined;
  }
  const rects = patternInstanceTransforms(frame).map(transform => boundsFromAffineRect(transform, content));
  return unionRects(rects) ?? undefined;
}

export function applyAffineToVectorNetwork(network: VectorNetwork, matrix: Affine2D): VectorNetwork {
  if (matrix === IDENTITY_AFFINE || (
    matrix.a === 1 && matrix.b === 0 && matrix.c === 0 && matrix.d === 1 && matrix.e === 0 && matrix.f === 0
  )) {
    return network;
  }

  return {
    ...network,
    vertices: network.vertices.map(vertex => transformVertex(vertex, matrix)),
    edges: network.edges.map(edge => transformEdge(edge, matrix)),
  };
}

function repeatInstanceTransforms(layout: FrameRepeatLayout, content: Rect): Affine2D[] {
  const count = Math.max(1, layout.count);
  switch (layout.mode) {
    case "column":
      return Array.from({ length: count }, (_, index) =>
        affineTranslation(0, index * (content.height + layout.gap)));
    case "grid": {
      const { columns, total } = resolveRepeatGrid(layout);
      const columnGap = layout.columnGap;
      const rowGap = layout.rowGap;
      return Array.from({ length: total }, (_, index) => {
        const column = index % columns;
        const row = Math.floor(index / columns);
        return affineTranslation(
          column * (content.width + columnGap),
          row * (content.height + rowGap),
        );
      });
    }
    case "radial": {
      // Petal 0 is the editable document child — it stays put (identity). Other
      // petals are rotations of that child around the ring center. The ring center
      // sits `radius` away from the content center along -startAngle, so the
      // existing child already lies on the circle at startAngle.
      const contentCenter = {
        x: content.x + content.width / 2,
        y: content.y + content.height / 2,
      };
      if (count <= 1) {
        return [IDENTITY_AFFINE];
      }
      const step = 360 / count;
      const ringRadius = layout.radius > 0
        ? layout.radius
        : Math.max(content.width, content.height);
      const startRadians = (layout.startAngle * Math.PI) / 180;
      const ringCenter = {
        x: contentCenter.x - Math.cos(startRadians) * ringRadius,
        y: contentCenter.y - Math.sin(startRadians) * ringRadius,
      };
      return Array.from({ length: count }, (_, index) => {
        if (index === 0) {
          return IDENTITY_AFFINE;
        }
        return affineRotation(index * step, ringCenter);
      });
    }
    case "row":
    default:
      return Array.from({ length: count }, (_, index) =>
        affineTranslation(index * (content.width + layout.gap), 0));
  }
}

function mirrorInstanceTransforms(layout: FrameMirrorLayout, content: Rect): Affine2D[] {
  const center = {
    x: content.x + content.width / 2,
    y: content.y + content.height / 2,
  };
  const halfGap = layout.gap / 2;
  const transforms: Affine2D[] = [IDENTITY_AFFINE];

  if (layout.axis === "horizontal" || layout.axis === "both") {
    // Reflect across a vertical axis just to the right of the content (+ gap).
    const axisX = content.x + content.width + halfGap;
    transforms.push(affineScale(-1, 1, { x: axisX, y: center.y }));
  }
  if (layout.axis === "vertical" || layout.axis === "both") {
    const axisY = content.y + content.height + halfGap;
    transforms.push(affineScale(1, -1, { x: center.x, y: axisY }));
  }
  if (layout.axis === "both") {
    const axisX = content.x + content.width + halfGap;
    const axisY = content.y + content.height + halfGap;
    transforms.push(multiplyAffine(
      affineScale(1, -1, { x: center.x, y: axisY }),
      affineScale(-1, 1, { x: axisX, y: center.y }),
    ));
  }

  return transforms;
}

/** Resolve grid dimensions. Explicit rows → full columns×rows; rows 0 → wrap `count` by columns. */
export function resolveRepeatGrid(layout: FrameRepeatLayout): { columns: number; rows: number; total: number } {
  const columns = Math.max(1, Math.round(layout.columns));
  if (layout.rows > 0) {
    const rows = Math.max(1, Math.round(layout.rows));
    return { columns, rows, total: columns * rows };
  }
  const total = Math.max(1, Math.round(layout.count));
  const rows = Math.max(1, Math.ceil(total / columns));
  return { columns, rows, total };
}

function transformVertex(vertex: VectorVertex, matrix: Affine2D): VectorVertex {
  const point = applyAffinePoint(matrix, vertex);
  return { ...vertex, x: point.x, y: point.y };
}

function transformEdge(edge: VectorEdge, matrix: Affine2D): VectorEdge {
  return {
    ...edge,
    from: {
      ...edge.from,
      handle: edge.from.handle ? applyAffinePoint(matrix, edge.from.handle) : undefined,
    },
    to: {
      ...edge.to,
      handle: edge.to.handle ? applyAffinePoint(matrix, edge.to.handle) : undefined,
    },
  };
}
